import { Checkbox, Select, Tooltip, Typography } from "antd";
import { useControllers } from "../../Contexts/dashboardHooks";
import type { ControllerKind } from "../../Processing/types";
import {
  bindingOptionValue,
  boundController,
  controllersForKind,
  parseBindingOption,
} from "../../Processing/controllerBindings";
import { usesSessionWindow } from "../constants";

const { Text } = Typography;

interface ControllerBindingRowProps {
  kind: ControllerKind;
  /** The controller this section currently follows, if any. */
  controllerId?: string;
  /** Subject sections only: follow the controller's athletes, or the positions they play. */
  mode?: "positions";
  onChange: (controllerId: string | undefined, mode?: "positions") => void;
  /**
   * Why this widget cannot take a "last N sessions" window, for the types that reject it.
   *
   * The backend declines the substitution for them, so the widget keeps its own dates.
   * The note appears only while the bound slicer is actually on that window — a follower
   * that silently stops following is worse than one that never did.
   */
  keepsOwnDatesOnLatestN?: string;
}

/**
 * The "Controlled by Slicer" header on a date or subject section.
 *
 * Checking it stores a POINTER to a Slicer (slicer). The section below then shows that
 * controller's values in place of the widget's own, which stay intact underneath and come
 * back when the user unchecks it.
 */
export function ControllerBindingRow({
  kind, controllerId, mode, onChange, keepsOwnDatesOnLatestN,
}: ControllerBindingRowProps) {
  const controllers = useControllers();
  const eligible = controllersForKind(controllers, kind);
  const isBound = controllerId != null && eligible.some((c) => c.uuid === controllerId);
  const noneAvailable = eligible.length === 0;

  const boundMisc = boundController(controllerId, kind, controllers)?.spec.slicer_builder_misc;
  const slicerUsesWindow = usesSessionWindow(boundMisc);

  const checkbox = (
    <Checkbox
      checked={isBound}
      disabled={noneAvailable}
      onChange={(e) => onChange(e.target.checked ? eligible[0]?.uuid : undefined)}
    >
      <Text className="awm-controller-binding-label">Controlled by Slicer</Text>
    </Checkbox>
  );

  return (
    <div className="awm-controller-binding">
      {noneAvailable ? (
        <Tooltip title="Add a Slicer widget to this dashboard first.">
          <span>{checkbox}</span>
        </Tooltip>
      ) : (
        checkbox
      )}

      {isBound && (
        <Select
          size="small"
          value={bindingOptionValue(controllerId, mode)}
          onChange={(value) => {
            const parsed = parseBindingOption(value);
            onChange(parsed.uuid, parsed.mode);
          }}
          // "[Underlying Positions]" is longer than the row; let the open list size to its
          // own content rather than to the input, and let the input take the full row.
          popupMatchSelectWidth={false}
          className="awm-controller-binding-select"
          title={
            eligible.find((c) => c.uuid === controllerId)
              ? `${eligible.find((c) => c.uuid === controllerId)!.label}${
                  mode === "positions" ? " [Underlying Positions]" : ""
                }`
              : undefined
          }
          options={eligible.flatMap((c) => {
            const name =
              eligible.filter((o) => o.label === c.label).length > 1
                ? `${c.label} (${c.uuid.slice(0, 4)})`
                : c.label;
            const base = { value: bindingOptionValue(c.uuid), label: name };
            // Subject sections can follow the POSITIONS of the controller's athletes
            // instead — so one controller drives both a player and their position average.
            return kind === "subject"
              ? [base, {
                  value: bindingOptionValue(c.uuid, "positions"),
                  label: `${name} [Underlying Positions]`,
                }]
              : [base];
          })}
        />
      )}

      {isBound && slicerUsesWindow && keepsOwnDatesOnLatestN && (
        <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 4 }}>
          This slicer is set to a session window, which this widget cannot use —
          {" "}{keepsOwnDatesOnLatestN}. It keeps its own date range below.
        </Text>
      )}
    </div>
  );
}
